import React, { useState, useEffect } from "react";
import Overview from "./components/Overview";
import ByPM from "./components/ByPM";
import ByOM from "./components/ByOM";
import DarAtencao from "./components/DarAtencao";
import Estrutura from "./components/Estrutura";
import ByMarca from "./components/ByMarca";
import AdminPanel from "./components/admin/AdminPanel";
import Login from "./components/Login";
import { fetchHealth, fetchMe, fetchProjectManagers, setViewAsPm } from "./services/api";
import logo from "./assets/logo.png";

const VISTAS = [
  { id: "bottlenecks", label: "Dar Atenção"                  },
  { id: "pm",          label: "Análise Project Manager"      },
  { id: "om",          label: "Análise Operational Manager"  },
  { id: "marca",       label: "Análise de Marca"             },
  { id: "estrutura",   label: "Visão de Equipa"              },
  { id: "overview",    label: "Overview"                     },
];

const VISTA_IDS = [...VISTAS.map((v) => v.id), "admin"];

function vistaFromHash() {
  const hash = window.location.hash.replace("#", "");
  return VISTA_IDS.includes(hash) ? hash : "bottlenecks";
}

function formatSyncDate(isoString) {
  const d = new Date(isoString);
  const agora = new Date();
  const diffMin = Math.floor((agora - d) / 60000);
  if (diffMin < 1) return "agora mesmo";
  if (diffMin < 60) return `há ${diffMin}m`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `há ${diffH}h`;
  return d.toLocaleDateString("pt-PT", { day: "numeric", month: "short" });
}

function AppLoading() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh", backgroundColor: "#f8fafc" }}>
      <div style={{ textAlign: "center", color: "#94a3b8" }}>
        <div style={loadingSpinnerStyle} />
        <p style={{ marginTop: 16, fontSize: 13 }}>A carregar…</p>
      </div>
    </div>
  );
}

const loadingSpinnerStyle = {
  width: 32,
  height: 32,
  border: "3px solid #e2e8f0",
  borderTop: "3px solid #6366f1",
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
  margin: "0 auto",
};

export default function App() {
  const [vistaAtiva, setVistaAtiva]       = useState(vistaFromHash);
  const [marcaInicial, setMarcaInicial]   = useState(null);
  const [healthStatus, setHealthStatus]   = useState(null);
  const [user, setUser]                   = useState(null);
  const [authLoading, setAuthLoading]     = useState(true);
  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [pmOptions, setPmOptions]         = useState([]);
  const [viewAsPmId, setViewAsPmId]       = useState(null);

  useEffect(() => {
    fetchMe()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setAuthLoading(false));

    fetchHealth()
      .then(setHealthStatus)
      .catch(() => setHealthStatus({ status: "error" }));
  }, []);

  // Lista de PMs para o seletor "Ver como" — carregada uma única vez, antes
  // de qualquer "ver como" estar ativo, para vir sempre completa.
  useEffect(() => {
    if (user?.role === "admin") {
      fetchProjectManagers().then(setPmOptions).catch(() => {});
    }
  }, [user]);

  if (authLoading) return <AppLoading />;
  if (!user) return <Login />;

  const efetivoComoPm = user.role === "admin" ? viewAsPmId : null;
  const roleEfetivo   = user.role === "pm" || efetivoComoPm != null ? "pm" : user.role;

  const irPara = (id) => {
    setVistaAtiva(id);
    window.location.hash = id;
    setSidebarAberta(false);
  };

  const mudarViewAs = (valor) => {
    const id = valor ? Number(valor) : null;
    setViewAsPmId(id);
    setViewAsPm(id);
    if (id != null && vistaAtiva === "estrutura") {
      irPara("overview");
    }
  };

  const navegarParaMarca = (planId) => {
    setMarcaInicial(planId);
    setVistaAtiva("marca");
    window.location.hash = "marca";
    setSidebarAberta(false);
  };

  const vistasVisiveis = VISTAS.filter((v) => !(v.id === "estrutura" && roleEfetivo === "pm"));

  const renderVista = () => {
    switch (vistaAtiva) {
      case "overview":    return <Overview onNavigateMarca={navegarParaMarca} />;
      case "estrutura":   return <Estrutura />;
      case "pm":          return <ByPM />;
      case "om":          return <ByOM />;
      case "marca":       return <ByMarca marcaInicial={marcaInicial} onClear={() => setMarcaInicial(null)} />;
      case "bottlenecks": return <DarAtencao roleEfetivo={roleEfetivo} />;
      case "admin":       return <AdminPanel />;
      default:            return <Overview onNavigateMarca={navegarParaMarca} />;
    }
  };

  return (
    <div className="app-container" style={styles.app}>
      {/* Backdrop — cobre conteúdo quando sidebar mobile está aberta */}
      {sidebarAberta && (
        <div className="sidebar-backdrop" onClick={() => setSidebarAberta(false)} />
      )}

      {/* Header mobile — logo à esquerda, título ao centro, hamburguer à direita */}
      <header className="mobile-topbar">
        <img src={logo} alt="Companhia das Soluções" style={{ height: 28 }} />
        <span style={styles.mobileTitle}>Report Dashboard</span>
        <button style={styles.hamburger} onClick={() => setSidebarAberta(true)} aria-label="Abrir menu">
          <span style={styles.hambLine} />
          <span style={styles.hambLine} />
          <span style={styles.hambLine} />
        </button>
      </header>

      {/* Barra lateral */}
      <nav className={`app-sidebar${sidebarAberta ? " sidebar-open" : ""}`} style={styles.sidebar}>
        {/* Cabeçalho do sidebar */}
        <div style={styles.logo}>
          {/* Desktop: logo + subtítulo normais */}
          {/* Mobile: logo + botão fechar lado a lado */}
          <div className="sidebar-mobile-header">
            <img src={logo} alt="Companhia das Soluções" style={{ height: 28 }} />
            <button
              style={styles.closeBtn}
              onClick={() => setSidebarAberta(false)}
              aria-label="Fechar menu"
            >
              ✕
            </button>
          </div>
          <img src={logo} alt="Companhia das Soluções" style={styles.logoImg} className="sidebar-logo-img" />
          <span style={styles.logoSub} className="sidebar-logo-sub">Report Dashboard</span>
        </div>

        <ul style={styles.navList}>
          {vistasVisiveis.map((v) => (
            <li key={v.id}>
              <button
                style={{
                  ...styles.navItem,
                  ...(vistaAtiva === v.id ? styles.navItemAtivo : {}),
                }}
                onClick={() => irPara(v.id)}
              >
                {v.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Admin (só visível para admins) */}
        {user.role === "admin" && (
          <div style={styles.adminSection}>
            <button
              style={{
                ...styles.navItem,
                ...(vistaAtiva === "admin" ? styles.navItemAtivo : {}),
              }}
              onClick={() => irPara("admin")}
            >
              Administração
            </button>
          </div>
        )}

        {/* Info do utilizador + estado do servidor */}
        <div style={styles.sidebarFooter}>
          {healthStatus && (
            <div style={styles.healthBadge}>
              <span
                style={{
                  ...styles.healthDot,
                  backgroundColor: healthStatus.status === "ok" ? "#4ade80" : "#f87171",
                }}
              />
              {healthStatus.status === "ok" ? "Servidor OK" : "Servidor com erros"}
            </div>
          )}
          {healthStatus?.ultimo_sync?.concluido_em && (
            <div style={styles.syncInfo}>
              Sync: {formatSyncDate(healthStatus.ultimo_sync.concluido_em)}
            </div>
          )}
          {process.env.REACT_APP_BUILD_DATE && (
            <div style={styles.versionInfo}>
              v {process.env.REACT_APP_BUILD_DATE}
            </div>
          )}
          {user.role === "admin" && pmOptions.length > 0 && (
            <div style={styles.verComoBox}>
              <label style={styles.verComoLabel}>Ver como</label>
              <select
                style={styles.verComoSelect}
                value={viewAsPmId ?? ""}
                onChange={(e) => mudarViewAs(e.target.value)}
              >
                <option value="">Normal (admin)</option>
                {pmOptions.map((pm) => (
                  <option key={pm.id} value={pm.id}>PM: {pm.nome}</option>
                ))}
              </select>
            </div>
          )}
          <div style={styles.userInfo}>
            <span style={styles.userName}>{user.nome}</span>
            <span style={styles.userRole}>{user.role}</span>
            <a href="/auth/logout" style={styles.logoutLink}>Sair</a>
          </div>
        </div>
      </nav>

      {/* Área de conteúdo — key força remount ao mudar "Ver como" para reforçar o fetch scoped */}
      <main className="main-content" style={styles.main} key={`${vistaAtiva}-${efetivoComoPm ?? "normal"}`}>
        {renderVista()}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
const styles = {
  app: {
    display: "flex",
    height: "100vh",
    overflow: "hidden",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    backgroundColor: "#f8fafc",
    color: "#1e293b",
  },
  sidebar: {
    width: 224,
    height: "100vh",
    backgroundColor: "#ffffff",
    borderRight: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    padding: "0 0 16px 0",
    flexShrink: 0,
    overflowY: "auto",
    position: "relative",
  },
  logo: {
    padding: "20px 20px 16px",
    borderBottom: "1px solid #f1f5f9",
    marginBottom: 6,
  },
  logoImg: {
    display: "block",
    width: "100%",
    maxWidth: 160,
    marginBottom: 6,
  },
  logoSub: {
    display: "block",
    fontSize: 10,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
  },
  navList: {
    listStyle: "none",
    margin: 0,
    padding: "6px 0",
    flex: 1,
  },
  navItem: {
    display: "flex",
    alignItems: "center",
    width: "calc(100% - 16px)",
    padding: "9px 14px",
    margin: "1px 8px",
    background: "none",
    border: "none",
    borderRadius: 6,
    color: "#475569",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    textAlign: "left",
    transition: "background 0.12s, color 0.12s",
  },
  navItemAtivo: {
    backgroundColor: "#6366f1",
    color: "#ffffff",
    fontWeight: 600,
  },
  adminSection: {
    borderTop: "1px solid #f1f5f9",
    paddingTop: 6,
    marginTop: 6,
  },
  sidebarFooter: {
    borderTop: "1px solid #f1f5f9",
    padding: "12px 20px 0",
    marginTop: "auto",
  },
  healthBadge: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 11,
    color: "#94a3b8",
    marginBottom: 8,
  },
  healthDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  verComoBox: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
    marginBottom: 10,
  },
  verComoLabel: {
    fontSize: 10,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  verComoSelect: {
    padding: "5px 8px",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    fontSize: 12,
    color: "#374151",
    backgroundColor: "#fff",
    cursor: "pointer",
  },
  userInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  userName: {
    fontSize: 12,
    color: "#374151",
    fontWeight: 500,
  },
  userRole: {
    fontSize: 10,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  logoutLink: {
    fontSize: 11,
    color: "#94a3b8",
    textDecoration: "none",
    marginTop: 4,
  },
  syncInfo: {
    fontSize: 10,
    color: "#cbd5e1",
    marginBottom: 6,
  },
  versionInfo: {
    fontSize: 10,
    color: "#cbd5e1",
    marginTop: 4,
  },
  main: {
    flex: 1,
    padding: 32,
    overflowY: "auto",
    height: "100vh",
  },
  mobileTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  sidebarMobileLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: "#374151",
  },
  closeBtn: {
    background: "#f1f5f9",
    border: "none",
    borderRadius: 6,
    width: 32,
    height: 32,
    cursor: "pointer",
    fontSize: 15,
    color: "#64748b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
  },
  // Hamburger button
  hamburger: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 5,
    width: 40,
    height: 40,
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "8px 6px",
    borderRadius: 6,
  },
  hambLine: {
    display: "block",
    width: "100%",
    height: 2,
    backgroundColor: "#374151",
    borderRadius: 2,
  },
};
